import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';

interface Template { id: string; name: string; serviceType: string; rateAmount: number; rateUnit: string; markupPct: number; timeline?: string; }

const card: any  = { background: 'white', borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const lbl:  any  = { display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14, color: '#374151' };
const inp:  any  = { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 16 };
  const blank = { name: '', serviceType: '', rateAmount: '', rateUnit: 'hour', markupPct: 15, timeline: '' };

export default function Settings() {
  // Company state
  const [company, setCompany] = useState({
    companyName: '', phone: '', address: '', website: '', taxNumber: '', brandColor: '#2563EB'
  });
  const [logoUrl,      setLogoUrl]      = useState('');
  const [savingCo,     setSavingCo]     = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);

  // Template state
  const [templates, setTemplates] = useState<Template[]>([]);
  const [editing,   setEditing]   = useState<any>(null);
  const [saving,    setSaving]    = useState(false);

  // Billing state
  const [billing, setBilling] = useState<any>(null);

  const loadTemplates = () => api.get('/templates').then(r => setTemplates(r.data));

  useEffect(() => {
    // Load company profile
    api.get('/company').then(r => {
      const d = r.data;
      setCompany({
        companyName: d.companyName || d.businessName || '',
        phone:       d.phone       || '',
        address:     d.address     || '',
        website:     d.website     || '',
        taxNumber:   d.taxNumber   || '',
        brandColor:  d.brandColor  || '#2563EB'
      });
      setLogoUrl(d.logoUrl || '');
    }).catch(console.error);

    loadTemplates();
    api.get('/billing/current').then(r => setBilling(r.data)).catch(() => {});
  }, []);

  // ── Company handlers ──────────────────────────────────────────
  const saveCompany = async () => {
    setSavingCo(true);
    try {
      await api.put('/company', company);
      toast.success('Company details saved!');
    } catch { toast.error('Failed to save'); } finally { setSavingCo(false); }
  };

  const uploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error('Logo must be under 2MB');
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const { data } = await api.post('/company/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoUrl(data.logoUrl);
      toast.success('Logo uploaded!');
    } catch { toast.error('Logo upload failed'); } finally { setUploadingLogo(false); }
  };

  const updCo = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setCompany(c => ({ ...c, [k]: e.target.value }));

  // ── Template handlers ─────────────────────────────────────────
  const saveTemplate = async () => {
    if (!editing.name || !editing.rateAmount) return toast.error('Name and rate are required');
    setSaving(true);
    try {
      const data = { ...editing, rateAmount: Math.round(Number(editing.rateAmount) * 100) };
      if (editing.id) await api.put(`/templates/${editing.id}`, data);
      else             await api.post('/templates', data);
      toast.success('Rate card saved!');
      setEditing(null);
      loadTemplates();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const delTemplate = async (id: string) => {
    if (!window.confirm('Delete this rate card?')) return;
    await api.delete(`/templates/${id}`);
    toast.success('Deleted');
    loadTemplates();
  };

  const upgrade = async (tier: string) => {
    try { const { data } = await api.post('/billing/create-checkout', { tier }); window.location.href = data.url; }
    catch { toast.error('Failed to open checkout'); }
  };

  const updTmpl = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setEditing((t: any) => ({ ...t, [k]: e.target.value }));

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      <nav style={{ background: 'white', padding: '16px 24px', display: 'flex', gap: 24, alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <Link to="/dashboard" style={{ color: '#2563EB', fontWeight: 700, textDecoration: 'none' }}>← Dashboard</Link>
        <span style={{ fontWeight: 700 }}>Settings</span>
      </nav>

      <div style={{ maxWidth: 680, margin: '32px auto', padding: '0 24px' }}>

        {/* ── Company Details ── */}
        <div style={card}>
          <h3 style={{ marginBottom: 4 }}>🏢 Company Details</h3>
          <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>This information appears on all your PDF quotes.</p>

          {/* Logo upload */}
          <div style={{ marginBottom: 20 }}>
            <label style={lbl}>Company Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {logoUrl
                ? <img src={logoUrl} alt="logo" style={{ height: 56, width: 'auto', borderRadius: 8, border: '1px solid #e5e7eb', objectFit: 'contain', background: '#f9fafb', padding: 4 }} />
                : <div style={{ height: 56, width: 120, borderRadius: 8, border: '2px dashed #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 12 }}>No logo</div>
              }
              <div>
                <button onClick={() => logoRef.current?.click()} disabled={uploadingLogo}
                  style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {uploadingLogo ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
                </button>
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>PNG, JPG or SVG. Max 2MB.</div>
              </div>
              <input ref={logoRef} type="file" accept="image/*" onChange={uploadLogo} style={{ display: 'none' }} />
            </div>
          </div>

          {/* Brand color */}
          <div style={{ marginBottom: 16 }}>
            <label style={lbl}>Brand Color</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="color" value={company.brandColor} onChange={updCo('brandColor')}
                style={{ width: 48, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
              <input value={company.brandColor} onChange={updCo('brandColor')}
                style={{ ...inp, width: 120, marginBottom: 0 }} placeholder="#2563EB" />
              <div style={{ fontSize: 13, color: '#666' }}>Used as accent color on PDF quotes</div>
            </div>
          </div>

          {/* Two column grid for fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <div>
              <label style={lbl}>Company Name</label>
              <input value={company.companyName} onChange={updCo('companyName')} style={inp} placeholder="MRC Ventures" />
            </div>
            <div>
              <label style={lbl}>Phone Number</label>
              <input value={company.phone} onChange={updCo('phone')} style={inp} placeholder="+880 17..." />
            </div>
            <div>
              <label style={lbl}>Website</label>
              <input value={company.website} onChange={updCo('website')} style={inp} placeholder="https://mrcventures.com" />
            </div>
            <div>
              <label style={lbl}>Tax / VAT Number</label>
              <input value={company.taxNumber} onChange={updCo('taxNumber')} style={inp} placeholder="Optional" />
            </div>
          </div>

          <label style={lbl}>Business Address</label>
          <textarea value={company.address} onChange={updCo('address')}
            style={{ ...inp, height: 72, resize: 'vertical' }}
            placeholder="123 Main Street, Dhaka, Bangladesh" />

          <button onClick={saveCompany} disabled={savingCo}
            style={{ padding: '12px 28px', background: '#2563EB', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 15 }}>
            {savingCo ? 'Saving...' : 'Save Company Details'}
          </button>
        </div>

        {/* ── Rate Cards ── */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3>💰 Rate Cards</h3>
            <button onClick={() => setEditing({ ...blank })}
              style={{ padding: '8px 16px', background: '#2563EB', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
              + Add Rate Card
            </button>
          </div>

          {templates.length === 0 && (
            <p style={{ color: '#999', textAlign: 'center', padding: 24 }}>No rate cards yet. Click "+ Add Rate Card" above.</p>
          )}

          {templates.map(t => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{t.name}</div>
                <div style={{ fontSize: 13, color: '#666' }}>${t.rateAmount / 100}/{t.rateUnit} • Markup: {t.markupPct}%{t.timeline ? ` • ⏱ ${t.timeline}` : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setEditing({ ...t, rateAmount: t.rateAmount / 100 })}
                  style={{ padding: '6px 12px', background: '#dbeafe', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#1d4ed8', fontSize: 13, fontWeight: 600 }}>Edit</button>
                <button onClick={() => delTemplate(t.id)}
                  style={{ padding: '6px 12px', background: '#fee2e2', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#dc2626', fontSize: 13, fontWeight: 600 }}>Delete</button>
              </div>
            </div>
          ))}
        </div>

        {/* Add/Edit Rate Card Form */}
        {editing !== null && (
          <div style={card}>
            <h3 style={{ marginBottom: 20 }}>{editing.id ? 'Edit Rate Card' : 'New Rate Card'}</h3>
            <label style={lbl}>Rate Card Name (e.g. Interior Painting)</label>
            <input value={editing.name} onChange={updTmpl('name')} style={inp} placeholder="Interior Painting" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div>
                <label style={lbl}>Rate Amount</label>
                <input value={editing.rateAmount} onChange={updTmpl('rateAmount')} type="number" style={inp} placeholder="50" />
              </div>
              <div>
                <label style={lbl}>Rate Unit</label>
                <select value={editing.rateUnit} onChange={updTmpl('rateUnit')} style={inp}>
                  {['hour','day','room','sqm','custom'].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <label style={lbl}>Markup %</label>
            <input value={editing.markupPct} onChange={updTmpl('markupPct')} type="number" style={inp} />
            <label style={lbl}>Typical Timeline (e.g. "2-3 days", "1 week")</label>
            <input value={editing.timeline || ''} onChange={updTmpl('timeline')} style={inp} placeholder="e.g. 2-3 days, 1 week, 4 hours" />
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={saveTemplate} disabled={saving}
                style={{ padding: '12px 24px', background: '#2563EB', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => setEditing(null)}
                style={{ padding: '12px 24px', background: '#f3f4f6', color: '#555', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Billing ── */}
        <div style={card}>
          <h3 style={{ marginBottom: 8 }}>💳 Billing</h3>
          <p style={{ color: '#666', marginBottom: 20 }}>Current plan: <strong>{billing?.tier || 'Free'}</strong> ({billing?.status || 'trial'})</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            {([['starter','$29/mo','Starter'],['pro','$79/mo','Pro ⭐'],['agency','$199/mo','Agency']] as [string,string,string][]).map(([tier, price, label]) => (
              <button key={tier} onClick={() => upgrade(tier)}
                style={{ padding: 16, border: `2px solid ${billing?.tier === tier ? '#2563EB' : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', background: billing?.tier === tier ? '#dbeafe' : 'white', fontWeight: 600 }}>
                <div>{label}</div>
                <div style={{ color: '#2563EB', marginTop: 4 }}>{price}</div>
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}